import { MultiSelect, type Option, SingleSelect } from "@appretio/ui-kit";
import type { ReactNode } from "react";
import React from "react";

interface ForecastContextDropdownProps {
  /** Filter name shown as the eyebrow label above the selected value. */
  label: string;
  /** Available options. */
  options: Option[];
  /** Currently selected value(s). */
  selected?: string | string[];
  /** Whether this is a multi-select dropdown. */
  multi?: boolean;
  /** Whether options are loading. */
  loading?: boolean;
  /** Whether the dropdown is in an error state. */
  error?: boolean;
  /** Whether the dropdown is disabled. */
  disabled?: boolean;
  /** Text shown when there are no results. */
  noResultsText?: string;
  /** Called when the selection changes. */
  onChange: (value: string | string[]) => void;
  /** Action icons rendered inside the trigger, between value and chevron. */
  triggerActions?: ReactNode;
  /** Content rendered in the popover footer (e.g. a "New version" action). */
  footerContent?: ReactNode | ((close: () => void) => ReactNode);
}

export const ForecastContextDropdown = React.memo(
  ({
    label,
    options,
    selected,
    multi,
    loading,
    error,
    disabled,
    noResultsText,
    onChange,
    triggerActions,
    footerContent
  }: ForecastContextDropdownProps) => {
    if (multi) {
      const value = Array.isArray(selected) ? selected : [];

      return (
        <MultiSelect
          label={label}
          options={options}
          value={value}
          loading={loading}
          disabled={disabled}
          onChange={(value) => onChange(value)}
          error={error}
          typeToSearch
          triggerActions={triggerActions}
          footerContent={footerContent}
        />
      );
    }

    const selectedOption =
      typeof selected === "string"
        ? options.find((opt) => opt.value === selected)
        : undefined;

    return (
      <SingleSelect
        options={options}
        label={label}
        selectedOption={selectedOption}
        loading={loading}
        disabled={disabled}
        onChange={(value) => onChange(value)}
        error={error}
        searchable={false}
        noResultsText={noResultsText}
        triggerActions={triggerActions}
        footerContent={footerContent}
      />
    );
  }
);
